/**
 * @file: constants.ts
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 */

import { BadgePalette, StatusPaletteKey } from './types';

// Design tokens, sourced from the reference HTML (variant D).
export const TOKENS = {
  cardBg: '#ffffff',
  cardRadius: 14,
  cardShadow: 'rgba(15, 23, 42, 0.08)',
  cardShadowOffsetY: 4,
  cardShadowBlur: 12,
  cardElevation: 4,
  divider: '#eef0f3',

  textPrimary: '#1f2937', // license plate, values
  textSecondary: '#4b5563', // step label
  textMuted: '#6b7280', // age, brand name
  textFaint: '#9ca3af', // grid labels, progress headers

  modelBadgeText: '#2563eb',
  modelBadgeBorder: '#bfdbfe',

  firstTimeBg: '#f59e0b',
  firstTimeText: '#ffffff',

  fuelGasBg: '#f3f4f6',
  fuelGasText: '#6b7280',
  fuelElectricBg: '#e6f7ec',
  fuelElectricText: '#0a7e3f',

  subscribedBg: '#e6f7ec',
  subscribedText: '#0a7e3f',
  unsubscribedBg: '#f3f4f6', // gray-100
  unsubscribedText: '#9ca3af', // gray-400

  progressBg: '#fafbfc',
  stepFinishedBg: '#0a7e3f',
  stepInProgressText: '#2563eb', // blue-600
  stepPendingBg: '#d1d5db',
  connectorActive: '#0a7e3f',
  connectorInactive: '#e5e7eb',

  viewStatusReadText: '#0a7e3f',
  viewStatusUnreadText: '#b91c1c',
  viewStatusPendingText: '#9ca3af',
};

// Palette used by the report-progress section when the task has timed out.
// Keeps green for finished steps (still a success state) and shifts everything
// else into the red family so the section reads as "expired" without losing
// hierarchy.
export const TIMED_OUT = {
  bg: '#fffbeb', // amber-50
  textPrimary: '#92400e', // amber-800
  textSecondary: '#b45309', // amber-700
  textFaint: '#d97706', // amber-600
  alertText: '#92400e', // (已超期) label
  stepPendingBg: '#fde68a', // amber-200
  stepInProgressText: '#2563eb', // blue-600 (kept consistent across states)
  connectorInactive: '#fef3c7', // amber-100
  viewStatusPendingText: '#d97706',
};

export const kLogoSize = 24;
export const kStepCircleSize = 18;
export const kStepColumnWidth = 52;

export const kStepKeys = [
  'preInspection',
  'inspection',
  'construction',
  'deliveryCheck',
] as const;

export const kStepLabels: Record<(typeof kStepKeys)[number], string> = {
  preInspection: '预检',
  inspection: '检测',
  construction: '施工',
  deliveryCheck: '交车',
};

export const StatusPalettes: Record<StatusPaletteKey, BadgePalette> = {
  blue: { text: '#2563eb', bg: '#eaf1fe' },
  cyan: { text: '#0891b2', bg: '#e0f3f8' },
  green: { text: '#0a7e3f', bg: '#e6f7ec' },
  gray: { text: '#6b7280', bg: '#f3f4f6' },
  amber: { text: '#b45309', bg: '#fef3c7' },
  red: { text: '#b91c1c', bg: '#fee2e2' },
  violet: { text: '#7c3aed', bg: '#ede9fe' },
};
